export const EntityWithIdFields = {
    Id: 'id'
} as const

export const EntityWithIdAndDescriptionFields = {
    ...EntityWithIdFields,
    Description: 'descripcion'
} as const

/**
 * Tipo de entidad básico.
 * @template TId - El tipo del id.
 * @default number - Tipo por defecto del id, de no especificarse.
 */
export type EntityWithId<TId = number> = {
    [EntityWithIdFields.Id]: TId
}

/**
 * Tipo de entidad con id y descripción.
 * @template TId - El tipo del id.
 * @default number - Tipo por defecto del id, de no especificarse.
 */
export type EntityWithIdAndDescription<TId = number> = EntityWithId<TId> & {
    [EntityWithIdAndDescriptionFields.Description]: string
}

/**
 * Campos para los tipos de objetos que descienden
 * del modelo de paginación.
 */
export const EntityPaginationFields = {
    CantPages: 'cantPages',
    ActualPage: 'actualPage',
    CantRecords: 'cantRecords',
    PageSize: 'pageSize',
    OrderBy: 'orderBy'
} as const

/**
 * Modelo de paginación.
 */
export type EntityPagination = {
    [EntityPaginationFields.CantPages]: number
    [EntityPaginationFields.ActualPage]: number
    [EntityPaginationFields.CantRecords]: number
    [EntityPaginationFields.PageSize]: number
}

/**
 * Filtro de paginación para request a la api.
 */
export type PaginacionRequest = {
    [EntityPaginationFields.ActualPage]: number
    [EntityPaginationFields.PageSize]: number
    [EntityPaginationFields.OrderBy]?: string
}

export const EntityListWithPaginationFields = {
    List: 'lista',
    Pagination: 'paginacion'
} as const

export type EntityListWithPagination<T> = {
    [EntityListWithPaginationFields.List]: T[]
    [EntityListWithPaginationFields.Pagination]: EntityPagination
}

export const BaseResponseFields = {
    Data: 'data',
    HasError: 'tieneError',
    ErrorDescription: 'descripcionError'
} as const

export type BaseResponse = {
    [BaseResponseFields.HasError]: boolean
    [BaseResponseFields.ErrorDescription]: string
}

export type BaseResponseWithData<T> = BaseResponse &{
    [BaseResponseFields.Data]: T
}

export const BaseRequestFields = {
    ModuleCode: 'codModulo',
    OriginCode: 'codOrigen'
} as const

export type BaseRequest = {
    [BaseRequestFields.ModuleCode]: number
    [BaseRequestFields.OriginCode]: number
}

export const MonedaFields = {
    Simbolo: 'simbolo'
} as const

export type Moneda = {
    [MonedaFields.Simbolo]: string
} & EntityWithIdAndDescription

export const PaisLogoFields = {
    LogoSvg: 'logoSvg',
    CodPais: 'codPais',
    Src: 'srcIcono'
} as const

export type PaisLogo = {
    [PaisLogoFields.CodPais]: number,
    [PaisLogoFields.LogoSvg]: string,
    [PaisLogoFields.Src]: string
} & EntityWithIdAndDescription

export enum SectorComercialFields {
    PaisCodigo = 'codPais',
    PaisNombre = 'descPais',
    SistemaCodigo = 'codSistema',
    SectorComercialCodigo = 'codSectorComercial',
    UnidadNegocioCodigo = 'codUnidadNegocio',
    SectorComercialTipo = 'codSectorComercialTipo'
}

export type SectorComercial = {
    [SectorComercialFields.PaisCodigo]: number
    [SectorComercialFields.PaisNombre]: string
    [SectorComercialFields.SistemaCodigo]: number
    [SectorComercialFields.SectorComercialCodigo]: number
    [SectorComercialFields.UnidadNegocioCodigo]: number
    [SectorComercialFields.SectorComercialTipo]: number
} & EntityWithIdAndDescription

export enum UnidadNegocioFields {
    UnidadNegocioTipo = 'codUnidadNegocioTipo'
}

export type UnidadNegocio = {
    [SectorComercialFields.PaisCodigo]: number
    [SectorComercialFields.PaisNombre]: string
    [SectorComercialFields.UnidadNegocioCodigo]: number
    [UnidadNegocioFields.UnidadNegocioTipo]: number
} & EntityWithIdAndDescription

export enum EntityFilterFields {
    PageSize = 'pageSize',
    CurrentPage = 'actualPage',
    OrderBy = 'orderBy',
}

export interface EntityFilter {
    [EntityFilterFields.PageSize]?: number;
    [EntityFilterFields.CurrentPage]?: number;
    [EntityFilterFields.OrderBy]?: string;
}